import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTasks } from '../hooks/useTasks';
import { useReminders } from '../hooks/useReminders';
import { useTimerNow } from '../hooks/useTaskTimers';
import { isReminderFiring } from '../lib/reminderDates';
import { useProjects } from '../hooks/useProjects';
import { useClients } from '../hooks/useClients';
import { clientLabel } from '../lib/clientLabel';
import { usePeople } from '../hooks/usePeople';
import { usePermissions } from '../hooks/usePermissions';
import {
  addDays,
  addMonths,
  dateKeyOf,
  fromDateKey,
  monthGrid,
  monthLabel,
  shortDayLabel,
  startOfWeekMonday,
  toDateKey,
  weekGrid,
} from '../lib/calendarDates';
import type { Task } from '../lib/tasksApi';
import type { Reminder } from '../lib/remindersApi';
import { CalendarToolbar, type CalendarView } from '../components/calendar/CalendarToolbar';
import { PostsCalendar } from '../components/socialPosts/PostsCalendar';
import { MonthGrid } from '../components/calendar/MonthGrid';
import { useMeetings } from '../hooks/useMeet';
import { useCalendarEvents } from '../hooks/useCalendarEvents';
import type { CalendarEvent } from '../lib/calendarApi';
import { useLeaveDays } from '../hooks/useLeave';
import type { LeaveDay } from '../lib/leaveApi';
import { useAuthStore } from '../store/useAuthStore';
import type { VideoMeetingSummary } from '../lib/meetApi';
import { WeekGrid } from '../components/calendar/WeekGrid';
import { GanttProjects } from '../components/calendar/GanttProjects';
import { GanttTeam } from '../components/calendar/GanttTeam';
import { FadeIn, SkeletonTable } from '../components/ui/Skeleton';
import { ToastProvider } from '../components/tasks/linear/Toast';
import { useI18n } from '../contexts/I18nContext';

// View mode persists across navigation, like the tasks list/board toggle.
const VIEW_KEY = 'cle:calendarView';
// /api/tasks caps pageSize at 200 — no pagination in v1, show a notice instead.
const TASKS_CAP = 200;
// Shared Gantt horizon: 12 weeks from the Monday of the current week.
const GANTT_WEEKS = 12;

function isView(raw: string | null): raw is CalendarView {
  return (
    raw === 'calendar' ||
    raw === 'week' ||
    raw === 'posts' ||
    raw === 'gantt-projects' ||
    raw === 'gantt-team'
  );
}

/** The URL wins (a shared link), then the last view used in this browser. */
function loadView(fromUrl: string | null): CalendarView {
  if (isView(fromUrl)) return fromUrl;
  try {
    const raw = localStorage.getItem(VIEW_KEY);
    return isView(raw) ? raw : 'calendar';
  } catch {
    return 'calendar';
  }
}

/** `YYYY-MM` → { year, month } (month 0-based), or null when malformed. */
function parseMonth(raw: string | null): { year: number; month: number } | null {
  const m = raw ? /^(\d{4})-(\d{2})$/.exec(raw) : null;
  if (!m) return null;
  const month = Number(m[2]) - 1;
  return month >= 0 && month <= 11 ? { year: Number(m[1]), month } : null;
}

/** `YYYY-MM-DD` → the Monday of that week, or null when malformed. */
function parseMonday(raw: string | null): Date | null {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const d = fromDateKey(raw);
  return Number.isNaN(d.getTime()) ? null : startOfWeekMonday(d);
}

/**
 * Operational calendar (company / project / person): a monthly grid of tasks
 * on their due dates plus two Gantt views — in-flight projects and the team's
 * allocations. Task filters live in the URL query; the active view persists in
 * localStorage. Tabs are permission-gated (tasks/projects/people modules).
 */
export function CalendarPage() {
  // The app has no global toast host; a refused drag on the grid reports here.
  return (
    <ToastProvider>
      <CalendarPageBody />
    </ToastProvider>
  );
}

function CalendarPageBody() {
  const { t, locale } = useI18n();
  const { can, isLoading: permissionsLoading } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  // View and period come from the URL when present (a shared or reloaded
  // link reopens exactly the same screen), else from today.
  const [view, setView] = useState<CalendarView>(() => loadView(searchParams.get('view')));
  const today = new Date();
  const [month, setMonth] = useState(
    () =>
      parseMonth(searchParams.get('month')) ?? {
        year: today.getFullYear(),
        month: today.getMonth(),
      },
  );
  // Week view anchor: the Monday of the active week.
  const [weekStart, setWeekStart] = useState(
    () => parseMonday(searchParams.get('week')) ?? startOfWeekMonday(new Date()),
  );
  // Gantt horizon paging: shift in 4-week steps, anchored on a Monday.
  const [ganttStart, setGanttStart] = useState(
    () => parseMonday(searchParams.get('from')) ?? startOfWeekMonday(new Date()),
  );

  const [projectFilter, setProjectFilter] = useState(() => searchParams.get('project') ?? '');
  const [assigneeFilter, setAssigneeFilter] = useState(() => searchParams.get('assignee') ?? '');
  const [clientFilter, setClientFilter] = useState(() => searchParams.get('client') ?? '');
  // Calendar de postări filters (the assignee one is shared with the task views).
  const csvParam = (key: string) => (searchParams.get(key) ?? '').split(',').filter(Boolean);
  const [postClients, setPostClients] = useState<string[]>(() => csvParam('clients'));
  const [postPlatforms, setPostPlatforms] = useState<string[]>(() => csvParam('platform'));
  const [postApproval, setPostApproval] = useState<string[]>(() => csvParam('approval'));

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch {
      // Non-fatal: persistence is a convenience only.
    }
  }, [view]);

  // Mirror every filter into the URL query — view, the period on screen, the
  // project and the person — so a link or a reload reopens the same screen.
  // Only the period of the active view is written, so the URL stays short.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const set = (key: string, value: string) => {
      if (value) next.set(key, value);
      else next.delete(key);
    };
    const monthKey = `${month.year}-${String(month.month + 1).padStart(2, '0')}`;
    set('view', view);
    set('month', view === 'calendar' || view === 'posts' ? monthKey : '');
    set('week', view === 'week' ? toDateKey(weekStart) : '');
    set('from', view === 'gantt-projects' || view === 'gantt-team' ? toDateKey(ganttStart) : '');
    set('project', projectFilter);
    set('assignee', assigneeFilter);
    set('client', view === 'posts' ? '' : clientFilter);
    set('clients', view === 'posts' ? postClients.join(',') : '');
    set('platform', view === 'posts' ? postPlatforms.join(',') : '');
    set('approval', view === 'posts' ? postApproval.join(',') : '');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [
    view,
    month,
    weekStart,
    ganttStart,
    projectFilter,
    assigneeFilter,
    clientFilter,
    postClients,
    postPlatforms,
    postApproval,
  ]);

  const showGanttProjects = can('projects', 'canView');
  const showGanttTeam = can('people', 'canViewCapacity');
  const showPosts = can('socialPosts', 'canView');
  // Fall back to the calendar if a persisted tab is no longer permitted.
  const effectiveView: CalendarView =
    (view === 'gantt-projects' && !showGanttProjects) ||
    (view === 'gantt-team' && !showGanttTeam) ||
    (view === 'posts' && !showPosts)
      ? 'calendar'
      : view;
  const isPosts = effectiveView === 'posts';

  const isWeek = effectiveView === 'week';

  // The visible month grid includes adjacent-month days — query its full range.
  const weeks = useMemo(() => monthGrid(month.year, month.month), [month]);
  const weekDays = useMemo(() => weekGrid(weekStart), [weekStart]);

  // Tasks are fetched for the range currently on screen (month grid or week).
  const rangeStart = isWeek ? weekDays[0].date : weeks[0][0].date;
  const rangeEnd = isWeek ? weekDays[6].date : weeks[5][6].date;

  const { data, isLoading, isError } = useTasks({
    dueFrom: toDateKey(rangeStart),
    dueTo: toDateKey(rangeEnd),
    projectId: projectFilter || undefined,
    clientId: clientFilter || undefined,
    assigneeId: assigneeFilter || undefined,
    sort: 'dueDate',
    order: 'asc',
    pageSize: TASKS_CAP,
  });

  const tasksByDay = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const task of data?.tasks ?? []) {
      if (!task.dueDate) continue;
      const key = dateKeyOf(task.dueDate);
      const bucket = map.get(key);
      if (bucket) bucket.push(task);
      else map.set(key, [task]);
    }
    return map;
  }, [data]);

  // Personal reminders overlay (violet chips). Bucketed by the LOCAL day of
  // their due instant — dueAt is a timestamp, so slicing the UTC ISO string
  // (dateKeyOf) would file an early-morning reminder under the previous day.
  // The assignee filter narrows these overlays too. Reminders are private to
  // the signed-in account, so they belong on the calendar of that person only:
  // narrowing to a colleague hides them all. Meetings show when the chosen
  // person has a seat at the table (host, invited or joined).
  const meId = useAuthStore((s) => s.user?.id ?? null);
  const showReminders = !assigneeFilter || assigneeFilter === meId;

  const { data: remindersData } = useReminders();
  // A reminder is on the calendar only while it is firing — from 10 minutes
  // before its time until the user acts on it in the red banner (Andrei,
  // 2026-09-30: "să nu apară încontinuu"). Same rule and clock as the banner,
  // ticking every 30s here rather than every second: a month of chips need
  // not re-render for a clock nobody is watching.
  const nowMs = useTimerNow(remindersData?.serverNow, 30_000);
  const openReminders = useMemo(
    () => (showReminders ? (remindersData?.reminders ?? []).filter((r) => !r.done) : []),
    [remindersData, showReminders],
  );
  const bucketReminders = (reminders: Reminder[]) => {
    const map = new Map<string, Reminder[]>();
    for (const reminder of reminders) {
      const key = toDateKey(new Date(reminder.dueAt));
      const bucket = map.get(key);
      if (bucket) bucket.push(reminder);
      else map.set(key, [reminder]);
    }
    return map;
  };
  const remindersByDay = useMemo(
    () => bucketReminders(openReminders.filter((r) => isReminderFiring(r, nowMs))),
    [openReminders, nowMs],
  );
  // Every open reminder of the day still counts in "Total estimat": it is time
  // on the plate whether or not its chip has appeared yet.
  const plannedRemindersByDay = useMemo(() => bucketReminders(openReminders), [openReminders]);

  // Firmio Meet meetings on the same range (sky chips). Bucketed by the LOCAL
  // day of their start, like reminders. For people without the module the
  // query is a 403, which react-query keeps as "no data" — the grid stays clean.
  const { data: meetData } = useMeetings({
    from: rangeStart.toISOString(),
    to: new Date(rangeEnd.getTime() + 24 * 60 * 60 * 1000).toISOString(),
    limit: 200,
    enabled: can('videoMeetings', 'canView'),
  });
  const meetingsByDay = useMemo(() => {
    const map = new Map<string, VideoMeetingSummary[]>();
    for (const meeting of meetData?.meetings ?? []) {
      if (assigneeFilter && !meeting.attendeeUserIds.includes(assigneeFilter)) continue;
      const key = toDateKey(new Date(meeting.scheduledAt));
      const bucket = map.get(key);
      if (bucket) bucket.push(meeting);
      else map.set(key, [meeting]);
    }
    return map;
  }, [meetData, assigneeFilter]);

  // The selected person's Google Calendar (People → Integrări), on the days
  // it fills (Andrei, 2026-09-30). Only with one person chosen: the unfiltered
  // grid is the company's work, not anyone's private agenda. Copies of Firmio
  // Meet meetings are left out — those already show as meeting chips. Someone
  // whose calendar the viewer may not see, or who has none connected, simply
  // adds nothing (a 403 is kept as "no data", never an error on screen).
  const { data: eventsData } = useCalendarEvents(
    {
      userId: assigneeFilter || undefined,
      startDate: rangeStart.toISOString(),
      endDate: new Date(rangeEnd.getTime() + 24 * 60 * 60 * 1000 - 1).toISOString(),
      excludeMeet: true,
    },
    Boolean(assigneeFilter),
  );
  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    if (!assigneeFilter) return map;
    const add = (key: string, event: CalendarEvent) => {
      const bucket = map.get(key);
      if (bucket) bucket.push(event);
      else map.set(key, [event]);
    };
    for (const event of eventsData?.events ?? []) {
      if (event.status === 'CANCELLED') continue;
      if (event.allDay) {
        // Google stores an all-day event as calendar dates with an exclusive
        // end, so it is filed under every date it covers, by the date itself
        // (dateKeyOf) rather than by the local instant of UTC midnight.
        const last = new Date(new Date(event.endTime).getTime() - 24 * 60 * 60 * 1000);
        for (let d = fromDateKey(dateKeyOf(event.startTime)); d <= last; d = addDays(d, 1)) {
          add(toDateKey(d), event);
        }
      } else {
        // A timed event goes under the local day it starts, like a meeting.
        add(toDateKey(new Date(event.startTime)), event);
      }
    }
    return map;
  }, [eventsData, assigneeFilter]);

  // Approved leave over the same range (Concedii), so a day on the grid says
  // who is away on it. Unlike the Google events above, this is fetched with or
  // without an assignee filter: unfiltered, the whole team's bands are the
  // point of a team calendar — you open it to find out who is out this week.
  // No permission check is needed either, because /api/leave/days narrows to
  // the caller when they lack `leave.canViewAll`.
  const { data: leaveDays } = useLeaveDays(
    toDateKey(rangeStart),
    toDateKey(rangeEnd),
    assigneeFilter || undefined,
  );
  const leaveByDay = useMemo(() => {
    const map = new Map<string, LeaveDay[]>();
    // The server already fanned each request across every date it covers, so
    // one row belongs to exactly one bucket — no span arithmetic here, unlike
    // the all-day Google events above.
    for (const leave of leaveDays ?? []) {
      const bucket = map.get(leave.day);
      if (bucket) bucket.push(leave);
      else map.set(leave.day, [leave]);
    }
    return map;
  }, [leaveDays]);

  const { data: projectsData } = useProjects({ limit: 200 });
  const { data: people } = usePeople();
  const { data: clientsData } = useClients({ limit: 100, active: 'true', sort: 'name' });
  const clientOptions = useMemo(
    () => (clientsData?.data ?? []).map((c) => ({ value: c.id, label: clientLabel(c) })),
    [clientsData],
  );

  const projectOptions = useMemo(
    () => (projectsData?.projects ?? []).map((p) => ({ value: p.id, label: p.name })),
    [projectsData],
  );
  // Task assignees are Users — offer the people with a linked account.
  const assigneeOptions = useMemo(
    () =>
      (people ?? [])
        .filter((p) => p.userId)
        .map((p) => ({ value: p.userId as string, label: p.fullName })),
    [people],
  );

  const isCalendar = effectiveView === 'calendar';
  const ganttEnd = addDays(ganttStart, GANTT_WEEKS * 7 - 1);
  const weekEnd = addDays(weekStart, 6);
  const periodLabel =
    isCalendar || isPosts
      ? monthLabel(locale, month.year, month.month)
      : isWeek
        ? `${shortDayLabel(locale, weekStart)} – ${shortDayLabel(locale, weekEnd)}`
        : `${shortDayLabel(locale, ganttStart)} – ${shortDayLabel(locale, ganttEnd)}`;

  function goToday() {
    const now = new Date();
    if (isCalendar || isPosts) setMonth({ year: now.getFullYear(), month: now.getMonth() });
    else if (isWeek) setWeekStart(startOfWeekMonday(now));
    else setGanttStart(startOfWeekMonday(now));
  }

  function shift(direction: 1 | -1) {
    if (isCalendar || isPosts) {
      setMonth((m) => {
        const next = addMonths(new Date(m.year, m.month, 1), direction);
        return { year: next.getFullYear(), month: next.getMonth() };
      });
    } else if (isWeek) {
      setWeekStart((s) => addDays(s, direction * 7));
    } else {
      setGanttStart((s) => addDays(s, direction * 28));
    }
  }

  if (permissionsLoading) {
    return (
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <SkeletonTable rows={6} />
      </div>
    );
  }

  if (!can('tasks', 'canView')) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <p className="text-sm text-gray-500">{t('calendar.noAccess')}</p>
      </div>
    );
  }

  const capped = (data?.pagination.total ?? 0) > TASKS_CAP;

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">{t('calendar.title')}</h1>
      </div>

      <div className="space-y-4">
        <CalendarToolbar
          view={effectiveView}
          onViewChange={setView}
          showGanttProjects={showGanttProjects}
          showGanttTeam={showGanttTeam}
          showPosts={showPosts}
          label={periodLabel}
          onPrev={() => shift(-1)}
          onToday={goToday}
          onNext={() => shift(1)}
          showFilters={isCalendar || isWeek}
          clientOptions={clientOptions}
          clientId={clientFilter}
          onClientChange={setClientFilter}
          projectOptions={projectOptions}
          projectId={projectFilter}
          onProjectChange={setProjectFilter}
          assigneeOptions={assigneeOptions}
          assigneeId={assigneeFilter}
          onAssigneeChange={setAssigneeFilter}
        />

        {(isCalendar || isWeek) && capped && (
          <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            {t('calendar.cappedNotice', { count: TASKS_CAP })}
          </p>
        )}

        {(isCalendar || isWeek) &&
          (isLoading ? (
            <SkeletonTable rows={6} />
          ) : isError ? (
            <p className="py-8 text-center text-sm text-red-600">{t('calendar.loadError')}</p>
          ) : (
            <FadeIn>
              {/* Unfiltered, the grid mixes everyone's work, so each chip says
                  whose it is; with one person selected that would be noise. */}
              {isWeek ? (
                <WeekGrid
                  days={weekDays}
                  tasksByDay={tasksByDay}
                  remindersByDay={remindersByDay}
                  plannedRemindersByDay={plannedRemindersByDay}
                  meetingsByDay={meetingsByDay}
                  eventsByDay={eventsByDay}
                  leaveByDay={leaveByDay}
                  showAssignee={!assigneeFilter}
                  nowMs={nowMs}
                />
              ) : (
                <MonthGrid
                  weeks={weeks}
                  tasksByDay={tasksByDay}
                  remindersByDay={remindersByDay}
                  meetingsByDay={meetingsByDay}
                  eventsByDay={eventsByDay}
                  leaveByDay={leaveByDay}
                  showAssignee={!assigneeFilter}
                />
              )}
            </FadeIn>
          ))}

        {isPosts && (
          <PostsCalendar
            year={month.year}
            month={month.month}
            filters={{
              clients: postClients,
              platforms: postPlatforms,
              approval: postApproval,
              assignee: assigneeFilter,
            }}
            onFiltersChange={(f) => {
              setPostClients(f.clients);
              setPostPlatforms(f.platforms);
              setPostApproval(f.approval);
              setAssigneeFilter(f.assignee);
            }}
          />
        )}

        {effectiveView === 'gantt-projects' && (
          <GanttProjects horizonStart={ganttStart} weeks={GANTT_WEEKS} />
        )}
        {effectiveView === 'gantt-team' && (
          <GanttTeam horizonStart={ganttStart} weeks={GANTT_WEEKS} />
        )}
      </div>
    </div>
  );
}
